import { useState } from 'react';
import type { InterviewTypeListItemDto } from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { DataPanel } from '@sara/ui/components/data-panel';
import { Tabs, TabsList, TabsTrigger } from '@sara/ui/components/tabs';
import { Link, type LinkProps } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';

interface InterviewTypeListProps {
  interviewTypes: InterviewTypeListItemDto[];
  getLinkProps: (type: InterviewTypeListItemDto) => LinkProps;
}

interface RowProps {
  type: InterviewTypeListItemDto;
  linkProps: LinkProps;
}

function Row({ type, linkProps }: RowProps) {
  return (
    <Link
      {...linkProps}
      className="group grid grid-cols-[1fr_auto] items-center gap-6 px-6 py-5 transition-colors hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
    >
      <div className="min-w-0">
        <div className="flex items-baseline gap-3">
          <p className="text-[15px] font-medium tracking-tight text-foreground transition-colors group-hover:text-primary">
            {type.name}
          </p>
          <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
            v{type.latestVersion}
          </span>
          {type.archivedAt ? (
            <Badge variant="outline" className="text-[10px]">
              Archived
            </Badge>
          ) : null}
        </div>
        {type.description && (
          <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{type.description}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-6 text-xs text-muted-foreground">
        <span className="tabular-nums">
          {new Date(type.createdAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
        <ArrowUpRight
          className="h-4 w-4 -translate-x-1 text-muted-foreground/60 transition-all group-hover:translate-x-0 group-hover:text-foreground"
          strokeWidth={1.5}
        />
      </div>
    </Link>
  );
}

type TemplateTab = 'active' | 'archived';

export function InterviewTypeList({ interviewTypes, getLinkProps }: InterviewTypeListProps) {
  const [tab, setTab] = useState<TemplateTab>('active');

  if (interviewTypes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
        <p className="font-display text-xl font-medium text-foreground">No templates yet.</p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Create your first template to define a conversation AI Interviewer can run.
        </p>
      </div>
    );
  }

  const visible = interviewTypes.filter((type) =>
    tab === 'archived' ? type.archivedAt !== null : type.archivedAt === null
  );

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={(v) => setTab(v as TemplateTab)}>
        <TabsList>
          <TabsTrigger value="active" data-testid="templates-tab-active">
            Active
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="templates-tab-archived">
            Archived
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
          <p className="font-display text-xl font-medium text-foreground">
            {tab === 'archived' ? 'No archived templates.' : 'No active templates.'}
          </p>
          <p className="mt-2 max-w-xs text-sm text-muted-foreground">
            {tab === 'archived'
              ? 'Templates you archive will show up here.'
              : 'Every template is archived. Unarchive one to make it available again.'}
          </p>
        </div>
      ) : (
        <DataPanel>
          <DataPanel.Header columns="1fr auto">
            <span>Name</span>
            <span>Created</span>
          </DataPanel.Header>
          <DataPanel.Body>
            <ul className="divide-y divide-border/60">
              {visible.map((type) => (
                <li key={type.id}>
                  <Row type={type} linkProps={getLinkProps(type)} />
                </li>
              ))}
            </ul>
          </DataPanel.Body>
          <DataPanel.Footer>
            <span className="tabular-nums">
              {visible.length} {visible.length === 1 ? 'template' : 'templates'}
            </span>
          </DataPanel.Footer>
        </DataPanel>
      )}
    </div>
  );
}
